/**
 * The kiosk's language RULES — which languages this merchant offers, and which one is active.
 *
 * Pure: every input is passed in, nothing reads the store. §10's split — `./selectors` names the
 * store keys, `./index` and `./hooks` are the two adapters, and this file holds the rules they
 * both run.
 *
 * 🔴 **Nothing about DIRECTION lives here.** `directionFor` / `isRtlLanguage` are in
 * `@rista/core/utils/i18n`, because they are not kiosk rules — POS web and POS mobile will read
 * the same ones. What is kiosk-only, and what this file owns, is where the language comes FROM:
 * a merchant config default that the customer may override for one session.
 */
import { DEFAULT_LANGUAGE } from "../../utils/i18n";
import type { KioskConfig } from "../config/types";

/** Re-exported so a caller needs one import for "the kiosk's language", default included. */
export { DEFAULT_LANGUAGE };

/**
 * The answer when the merchant has configured nothing — the language every catalogue is
 * authored in.
 *
 * 🔴 **A module constant, not a fresh `[DEFAULT_LANGUAGE]` per call.** `offeredLanguages` is
 * read through a `useSelector`, which compares by IDENTITY, so a new array each time makes
 * every store notification look like a change. `config/selectors.ts` has the long account of
 * what that costs — it repainted the banner carousel on every tap of every card. Returning the
 * config's own array in the configured case and this one constant otherwise is what keeps this
 * a plain `(state) => value` selector with no memoisation, per `channel/selectors.ts`.
 *
 * ⚠️ SHARED, so nothing may mutate what `offeredLanguages` returns. Copy before sorting.
 */
export const ONLY_DEFAULT_LANGUAGE: readonly string[] = Object.freeze([DEFAULT_LANGUAGE]);

/**
 * The languages on offer, always non-empty.
 *
 * ABSENT MEANS `["en"]` — the same rule `banners`, `popularSearches` and `customerPhoneNumber`
 * follow, and this is the one place it is written down.
 *
 * ⚠️ **`languages` is authoritative.** A `defaultLanguage` that is not in it is IGNORED, not
 * promoted into the list: honouring it would boot the kiosk into a language the picker cannot
 * show, so a customer who left it could never get back. A merchant who wants Arabic only writes
 * `languages: ["ar"]`, which is the obvious thing to write and does the obvious thing.
 */
export function offeredLanguages(config: KioskConfig | null | undefined): readonly string[] {
  const listed = config?.languages;
  return listed && listed.length ? listed : ONLY_DEFAULT_LANGUAGE;
}

/**
 * The active language: the customer's choice, else the merchant's default, else the first on
 * offer. Always one of `offeredLanguages`, and never undefined.
 *
 * ⚠️ **Both candidates are checked AGAINST the offered list**, which is what makes the picker
 * and the screen agree — and what catches a stale override after a config refresh drops a
 * language mid-session.
 */
export function resolveLanguage(
  override: string | null | undefined,
  config: KioskConfig | null | undefined,
): string {
  const offered = offeredLanguages(config);
  if (override && offered.includes(override)) return override;
  const configured = config?.defaultLanguage;
  if (configured && offered.includes(configured)) return configured;
  return offered[0];
}

/**
 * Is there a choice to offer?
 *
 * This is the LIST's question, not a feature flag's — the flag that hides the picker at ship
 * time is separate and is checked beside this. Both must say yes. One configured language means
 * no picker for every merchant today, which is the point: the control cannot appear by accident.
 */
export function isLanguageChoiceOffered(config: KioskConfig | null | undefined): boolean {
  return offeredLanguages(config).length > 1;
}
